import React, { createRef } from "react";
import PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom";
import { Breadcrumb, Button, Container, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faDownload,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
    AreaChart,
    Area,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";

import MyTable from "components/MyTable";
import Emitter from "services/Emitter";
import WebsiteService from "services/WebsiteService";
import Toast from "services/Toast";
import Utils from "services/Utils";

/**
 * @component
 * @category Scenes
 * @subcategory Websites
 */
class Websites extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            days: ["...", "...", "...", "...", "...", "...", "..."],
            pdEngines: [],
            volume: null,
        };
        this.tableRef = createRef();
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);
        this.reload = this.reload.bind(this);
        this.chart = this.chart.bind(this);
    }

    downloadSpreadsheet() {
        this.tableRef.current.CSV();
    }

    componentWillUnmount() {
        Emitter.off("LOAD_BUCKET_PROFILE", this.reload);
    }

    componentDidMount() {
        Emitter.on("LOAD_BUCKET_PROFILE", this.reload);
        if (this.props.engine || this.props.bucket) {
            this.reload();
        }
    }

    reload() {
        this.setState({ loading: true, volume: null });
        this.tableRef.current?.clear();
        WebsiteService.bucket(this.props.engine, this.props.bucket).then(
            (data) => {
                if (data === null) return;
                if (data === false) {
                    Toast.error(
                        "Something went wrong while loading the list of websites"
                    );
                    return;
                }

                const pdEngines = {};
                const list = data.items.map((row) => {
                    const pds = row.pd?.split(",") ?? [];
                    pds.forEach((pd) => {
                        pdEngines[pd] = true;
                    });
                    return {
                        id: row.id,
                        name: row.name,
                        pd: pds.join(", "),
                        merchant_type: row.merchant_type,
                        mappings: row.mappings,
                        functional: row.functional ? 1 : 0,
                        day0: row.trends[0],
                        day1: row.trends[1],
                        day2: row.trends[2],
                        day3: row.trends[3],
                        day4: row.trends[4],
                        day5: row.trends[5],
                        day6: row.trends[6],
                        sum:
                            row.trends[1] +
                            row.trends[2] +
                            row.trends[3] +
                            row.trends[4] +
                            row.trends[5] +
                            row.trends[6],
                    };
                });

                this.setState(
                    {
                        days: data.trends,
                        pdEngines: Object.keys(pdEngines),
                        loading: false,
                    },
                    () => {
                        this.tableRef.current?.populate(list);
                    }
                );
            }
        );

        WebsiteService.bucketDataOverTime(
            this.props.engine,
            this.props.bucket
        ).then((data) => {
            if (data === null) return;
            if (data === false) {
                Toast.error(
                    "Something went wrong while loading the crawled listings over time"
                );
                return;
            }

            const volume = [];
            Object.keys(data).forEach((key) => {
                volume.push({
                    name: key,
                    total: data[key],
                });
            });

            this.setState({ volume });
        });
    }

    get renderColumns() {
        return {
            name: (row) => (
                <Link
                    className="link"
                    to={`/website/${row.id}`}
                    target="_blank"
                >
                    {row.name}
                </Link>
            ),
            functional: {
                className: "text-center",
                format: (row) => {
                    const icon = row.functional ? faCheck : faTimes;
                    const color = row.functional ? "green" : "red";

                    return <FontAwesomeIcon style={{ color }} icon={icon} />;
                },
                csv: (row) => (row.functional ? "Yes" : "No"),
            },
            day0: {
                className: "gray",
                format: (row) => (row.day0 ? row.day0 : ""),
            },
            day1: (row) => Utils.trendColumn(row, "day1"),
            day2: (row) => Utils.trendColumn(row, "day2"),
            day3: (row) => Utils.trendColumn(row, "day3"),
            day4: (row) => Utils.trendColumn(row, "day4"),
            day5: (row) => Utils.trendColumn(row, "day5"),
            day6: (row) => Utils.trendColumn(row, "day6"),
            sum: (row) => Utils.trendColumn(row, "sum"),
        };
    }

    get headers() {
        return [
            {
                field: "id",
                label: "ID",
                searchable: true,
                sortable: true,
                width: "50px",
            },
            {
                field: "name",
                label: "Website",
                searchable: true,
                sortable: true,
            },
            {
                field: "merchant_type",
                label: "Type",
                sortable: true,
                allowEmpty: true,
                options: ["Retailer", "Marketplace"],
            },
            {
                field: "mappings",
                label: "# Mappings",
                sortable: true,
                searchable: true,
                width: "100px",
            },
            {
                field: "pd",
                label: "PD Engines",
                sortable: true,
                allowEmpty: true,
                options: this.state.pdEngines,
                width: "100px",
            },
            {
                field: "functional",
                label: "Functional",
                sortable: true,
                allowEmpty: true,
                options: [
                    { label: "Yes", value: 1 },
                    { label: "No", value: 0 },
                ],
                width: "100px",
            },
            {
                field: "day0",
                width: "80px",
                hint: "Reference date",
                label: this.state.days[0],
                sortable: true,
                searchable: true,
            },
            {
                field: "day1",
                width: "80px",
                hint: `Variation between ${this.state.days[0]} and ${this.state.days[1]}`,
                label: this.state.days[1],
                sortable: true,
                searchable: true,
            },
            {
                field: "day2",
                width: "80px",
                hint: `Variation between ${this.state.days[1]} and ${this.state.days[2]}`,
                label: this.state.days[2],
                sortable: true,
                searchable: true,
            },
            {
                field: "day3",
                width: "80px",
                hint: `Variation between ${this.state.days[2]} and ${this.state.days[3]}`,
                label: this.state.days[3],
                sortable: true,
                searchable: true,
            },
            {
                field: "day4",
                width: "80px",
                hint: `Variation between ${this.state.days[3]} and ${this.state.days[4]}`,
                label: this.state.days[4],
                sortable: true,
                searchable: true,
            },
            {
                field: "day5",
                width: "80px",
                hint: `Variation between ${this.state.days[4]} and ${this.state.days[5]}`,
                label: this.state.days[5],
                sortable: true,
                searchable: true,
            },
            {
                field: "day6",
                width: "80px",
                hint: `Variation between ${this.state.days[5]} and ${this.state.days[6]}`,
                label: this.state.days[6],
                sortable: true,
                searchable: true,
            },
            {
                field: "sum",
                label: "Sum",
                width: "80px",
                hint: "Cumulative variation over the last week",
                sortable: true,
                searchable: true,
            },
        ];
    }

    chart() {
        if (this.state.volume === null) return null;

        return (
            <React.Fragment>
                <p style={{ textAlign: "center" }}>Unique Listings Over Time</p>
                <Row>
                    <Col>
                        <AreaChart
                            width={1280}
                            height={350}
                            data={this.state.volume}
                            margin={{ top: 0, right: 20, bottom: 5, left: 0 }}
                        >
                            <Area
                                type="monotone"
                                dataKey="total"
                                stroke="#8884d8"
                            />
                            <CartesianGrid
                                stroke="#ccc"
                                strokeDasharray="5 5"
                            />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                        </AreaChart>
                    </Col>
                </Row>
            </React.Fragment>
        );
    }

    render() {
        return (
            <Container className="pt-4">
                <span
                    style={{
                        float: "right",
                    }}
                >
                    <Button
                        disabled={this.state.loading}
                        size="sm"
                        className="tdButton"
                        variant="success"
                        label="Download CSV"
                        onClick={this.downloadSpreadsheet}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download table
                    </Button>
                </span>
                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Profiles</Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/bucket">Bucket</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>
                        {this.props.engine} - {this.props.bucket}
                    </Breadcrumb.Item>
                </Breadcrumb>
                <this.chart />
                <MyTable
                    name="bucket-website-list"
                    ref={this.tableRef}
                    headers={this.headers}
                    renderColumns={this.renderColumns}
                    sortBy="mappings"
                    sortOrder="desc"
                />
            </Container>
        );
    }
}

Websites.propTypes = {
    bucket: PropTypes.string.isRequired,
    engine: PropTypes.string.isRequired,
};

export default withRouter(Websites);
